import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { newsItems as newsItemsEn } from "@/mocks/en/newsItems";
import { newsItems as newsItemsZh } from "@/mocks/zh-Hant/newsItems";

export default function NewsSection() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const newsItems = i18n.language === "zh-Hant" ? newsItemsZh : newsItemsEn;

  const handleNav = (href: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    if (href.startsWith("http://") || href.startsWith("https://")) {
      window.open(href, "_blank", "noopener,noreferrer");
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate(href);
  };

  const isExternal = (href: string) =>
    href.startsWith("http://") || href.startsWith("https://");

  return (
    <section id="news" className="w-full py-20 md:py-28 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
          <div>
            <h2 className="font-heading font-bold text-foreground-950 text-3xl md:text-5xl leading-tight">
              {t("news.title")}
            </h2>
            <p className="text-foreground-600 mt-4 max-w-2xl text-sm md:text-base leading-relaxed">
              {t("news.description")}
            </p>
          </div>
          <a
            href="/news/articles"
            onClick={handleNav("/news/articles")}
            className="inline-flex items-center gap-2 text-primary-700 hover:text-primary-500 text-sm font-medium tracking-wider whitespace-nowrap cursor-pointer group"
          >
            {t("news.viewAll")}
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center transition-transform group-hover:translate-x-1"></i>
          </a>
        </div>

        {/* News items */}
        <div className="space-y-16 md:space-y-24">
          {newsItems.map((item, idx) => (
            <article
              key={item.id}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-14 items-center ${
                item.reverse ? "lg:[&>div:first-child]:order-2" : ""
              }`}
            >
              <div className="relative">
                <div className="relative aspect-[4/3] rounded-lg overflow-hidden border border-background-300/60">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="absolute top-4 left-4 px-3 py-1 rounded-md bg-background-50/95 text-primary-700 text-xs font-medium tracking-wider">
                  {item.tag}
                </div>
                <div className="absolute -bottom-4 -left-4 hidden md:flex w-16 h-16 items-center justify-center rounded-lg bg-accent-500 text-background-50 font-heading font-bold text-xl">
                  {String(idx + 1).padStart(2, "0")}
                </div>
              </div>
              <div className="lg:px-6">
                <div className="text-secondary-700 text-xs tracking-widest mb-3">
                  {item.reverse ? t("news.category.speech") : t("news.category.event")}
                </div>
                <h3 className="font-heading font-bold text-foreground-950 text-2xl md:text-3xl lg:text-4xl leading-tight mb-5">
                  {item.title}
                </h3>
                <p className="text-foreground-700 text-base leading-relaxed mb-6">{item.excerpt}</p>
                <a
                  href={item.link}
                  onClick={handleNav(item.link)}
                  target={isExternal(item.link) ? "_blank" : undefined}
                  rel={isExternal(item.link) ? "noopener noreferrer" : undefined}
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-primary-600 text-primary-700 hover:bg-primary-600 hover:text-background-50 rounded-md text-sm font-medium tracking-wider whitespace-nowrap cursor-pointer transition-colors"
                >
                  {t("news.learnMore")}
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}